import { InviteSelectDto } from '@@types/api.types';
import { Modal } from '@components/core/modal.component.tsx';
import { Button } from '@components/ui/button';
import { useAcceptInvite, useRejectInvite } from '@queries/invite.query.ts';
import { BuildingIcon } from 'lucide-react';
import { FunctionComponent } from 'react';

type CompanyProfilePendingInvitesModalProps = {
  invites: InviteSelectDto[];
  isOpen: boolean;
  onClose: () => void;
  onInviteAccepted: () => void;
  onInviteRejected: () => void;
};

const CompanyLogo = (props: { logoUrl?: string }) => {
  const { logoUrl } = props;

  return (
    <div className="shrink-0 size-10 bg-gray-100 rounded-xl flex items-center justify-center">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt="Company Logo"
        />
      ) : (
        <BuildingIcon />
      )}
    </div>
  );
};

export const CompanyProfilePendingInvitesModal: FunctionComponent<CompanyProfilePendingInvitesModalProps> = ({
  invites,
  isOpen,
  onClose,
  onInviteAccepted,
  onInviteRejected,
}) => {
  const { mutateAsync: acceptInvite } = useAcceptInvite();
  const { mutateAsync: rejectInvite } = useRejectInvite();

  const handleAccept = async (invite: InviteSelectDto) => {
    try {
      await acceptInvite(invite.id);
      onInviteAccepted();
    } catch (error) {
      console.error('Error accepting invite:', error);
    }
  };

  const handleDecline = async (invite: InviteSelectDto) => {
    try {
      await rejectInvite(invite.id);
      onInviteRejected();
    } catch (error) {
      console.error('Error declining invite:', error);
    }
  };

  return (
    <Modal
      centered
      isOpen={isOpen}
      onClose={onClose}
      width="500px"
    >
      <div className="flex w-full flex-col items-start border-b pb-4">
        <div className="font-medium text-gray-900 text-lg">Pending invitations</div>
        <div className="font-light text-gray-500 text-sm">
          You have been invited to collaborate in an existing company.
        </div>
      </div>
      <div className="flex w-full flex-col items-start gap-2">
        {invites.map((invite) => (
          <div
            key={invite.id}
            className="flex w-full flex-row items-center gap-2 rounded-lg bg-gray-100 p-3"
          >
            <div className="flex flex-row items-center gap-2">
              <CompanyLogo />
              <div className="flex flex-col">
                <div className="font-medium text-gray-900 text-sm">{invite.company.name}</div>
                <div className="font-light text-gray-500 text-xs">{invite.role}</div>
              </div>
            </div>
            <div className="ml-auto flex flex-row items-center justify-end gap-2">
              <Button
                onClick={() => handleDecline(invite)}
                variant="destructive"
              >
                Decline
              </Button>
              <Button
                onClick={() => handleAccept(invite)}
                variant="primary"
              >
                Accept
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
};
